import { useCallback, useEffect, useState } from 'react';
import { API_PREFIX } from '@/lib/api';

export type PushPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export interface UsePushSubscription {
  /** Browser permission / support state. */
  permission: PushPermissionState;
  /** Whether this device currently has an active push subscription. */
  subscribed: boolean;
  /** True while an enable()/disable() call is in flight. */
  busy: boolean;
  /**
   * Subscribe this device. MUST be invoked from a user gesture (iOS requires it,
   * and only works in an installed PWA). No-op when unsupported or the server
   * has no VAPID key configured (push ships dormant).
   */
  enable: () => Promise<void>;
  /** Unsubscribe this device (server + browser). */
  disable: () => Promise<void>;
}

/**
 * Decode a base64url VAPID key into the ArrayBuffer-backed bytes
 * PushManager.subscribe wants. Backed by an explicit ArrayBuffer so the result
 * is a `BufferSource` (not a possibly-SharedArrayBuffer-backed Uint8Array).
 */
function urlBase64ToBytes(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) {
    view[i] = raw.charCodeAt(i);
  }
  return buffer;
}

/**
 * Subscribe/unsubscribe this device for web-push approval-gate notifications.
 * SSR-safe (guards `window`/`navigator`) and degrades gracefully when the
 * browser lacks support or the server is unconfigured.
 */
export function usePushSubscription(): UsePushSubscription {
  const supported =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const [permission, setPermission] = useState<PushPermissionState>(() =>
    supported ? (Notification.permission as PushPermissionState) : 'unsupported'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    void navigator.serviceWorker.ready
      .then(reg => reg.pushManager.getSubscription())
      .then(sub => {
        if (!cancelled) setSubscribed(sub !== null);
      })
      .catch((error: unknown) => {
        // Reading existing-subscription state is best-effort; a failure here
        // just leaves `subscribed` false and is logged, never thrown to render.
        console.error('[push] failed to read existing subscription', error);
      });
    return (): void => {
      cancelled = true;
    };
  }, [supported]);

  const enable = useCallback(async (): Promise<void> => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm as PushPermissionState);
      if (perm !== 'granted') return;

      const keyRes = await fetch(`${API_PREFIX}/api/push/vapid-public-key`, {
        credentials: 'include',
      });
      if (!keyRes.ok) {
        console.error('[push] vapid-public-key request failed', keyRes.status);
        return;
      }
      const { key } = (await keyRes.json()) as { key: string | null };
      if (!key) return; // server has no VAPID configured -> inert by design

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToBytes(key),
      });
      const json = sub.toJSON();
      const p256dh = json.keys?.p256dh;
      const auth = json.keys?.auth;
      if (!json.endpoint || !p256dh || !auth) {
        console.error('[push] subscription is missing endpoint/keys');
        return;
      }
      const res = await fetch(`${API_PREFIX}/api/push/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: json.endpoint,
          keys: { p256dh, auth },
          userAgent: navigator.userAgent,
        }),
      });
      if (!res.ok) {
        // The browser sub created by pushManager.subscribe() above is intentionally
        // kept alive even though the server has no record: subscribe() is idempotent,
        // so a later retry reuses the same endpoint and succeeds. Unsubscribing here
        // would force the user to re-grant notification permission on the next attempt.
        console.error('[push] subscribe request failed', res.status);
        return;
      }
      setSubscribed(true);
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  const disable = useCallback(async (): Promise<void> => {
    if (!supported || busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setSubscribed(false);
        return;
      }
      const res = await fetch(`${API_PREFIX}/api/push/unsubscribe`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
      if (!res.ok) {
        // Server-side removal failed; surface it but still drop the browser
        // subscription so the device stops receiving pushes.
        console.error('[push] unsubscribe request failed', res.status);
      }
      await sub.unsubscribe();
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  }, [supported, busy]);

  return { permission, subscribed, busy, enable, disable };
}
